// ZapFeed.js — Bold modern redesign (Vercel/Stripe aesthetic)
// Typography: Bricolage Grotesque (display) · DM Sans (body)
// Spacing: strict 8px grid, px:2 py:1.75 everywhere
// Color: single primary accent, no rainbow

import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import {
  Box, Typography, Avatar, List, IconButton,
  Skeleton, TextField, Button, Snackbar, Alert, CircularProgress, Tooltip,
  Menu, MenuItem, ListItemIcon, ListItemText, Chip, Tabs, Tab, Collapse,
} from "@mui/material";
import { useTheme, alpha, keyframes } from "@mui/material/styles";
import { useMediaQuery }   from "@mui/material";
import { useNavigate }     from "react-router-dom";
import { useInfiniteQuery, useMutation, useQueryClient } from "react-query";
import {
  Image         as ImageIcon,
  Close         as CloseIcon,
  ContentCopy   as CopyIcon,
  Visibility    as ViewIcon,
  OpenInNew     as OpenInNewIcon,
  Link          as LinkIcon,
  Campaign      as CampaignIcon,
  PushPin       as PinIcon,
  EmojiEvents   as TrophyIcon,
  AutoAwesome   as SparkleIcon,
  ArrowUpward   as NewPostsIcon,
} from "@mui/icons-material";
import { Capacitor }   from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";

import API                from "../../../../API";
import { queryKeys }      from "../../../../API/queryKeys";
import { useCreatorAuth } from "../../../../Hooks/creator-use-auth";
import useAuth            from "../../../../Hooks/use-auth";
import FeedItem           from "./components/unitedfeed";
import ThreadShareDialog  from "./components/ThreadShareDialog";
import UploadFileDialog   from "../../../../Components/tools/uploadfile";
import CastingCallDialog  from "../../../../Pages/Dashboard/Campaigns/castingcalls/castingcalldialog";

/* ─────────────────────────────────────────────────────────
   Constants
───────────────────────────────────────────────────────── */
const ROW_COUNT = 20;
const MAX_CHARS = 280;
const MOBILE_NAV_OFFSET = 98;

/* ─────────────────────────────────────────────────────────
   Design tokens
───────────────────────────────────────────────────────── */
const FONT = {
  display: "'Bricolage Grotesque', sans-serif",
  body:    "'DM Sans', sans-serif",
};

/* ─────────────────────────────────────────────────────────
   Keyframes
───────────────────────────────────────────────────────── */
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0);   }
`;
const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;
const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0);    }
`;
const bounceIn = keyframes`
  0%   { opacity: 0; transform: translateY(-10px) scale(0.95); }
  60%  { transform: translateY(2px) scale(1.01); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
`;

/* ─────────────────────────────────────────────────────────
   Font injection
───────────────────────────────────────────────────────── */
const FONT_STYLE = `
  @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,500;12..96,600;12..96,700;12..96,800&family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&display=swap');
`;

/* ─────────────────────────────────────────────────────────
   Helpers
───────────────────────────────────────────────────────── */
function scrollAboveNav(el, isMobile) {
  if (!el) return;
  const extra  = isMobile ? MOBILE_NAV_OFFSET + 16 : 16;
  const rect   = el.getBoundingClientRect();
  const hidden = rect.bottom - (window.innerHeight - extra);
  if (hidden > 0) window.scrollBy({ top: hidden, behavior: "smooth" });
}

/* ─────────────────────────────────────────────────────────
   CharCircle
───────────────────────────────────────────────────────── */
function CharCircle({ length }) {
  const theme     = useTheme();
  const pct       = Math.min(length / MAX_CHARS, 1);
  const r         = 9;
  const circ      = 2 * Math.PI * r;
  const dash      = pct * circ;
  const nearLimit = length > MAX_CHARS * 0.8;
  const over      = length > MAX_CHARS;
  const color     = over ? "#ef4444" : nearLimit ? "#f59e0b" : theme.palette.primary.main;
  const remaining = MAX_CHARS - length;
  if (length === 0) return null;
  return (
    <Box sx={{ position: "relative", width: 26, height: 26, flexShrink: 0 }}>
      <svg width={26} height={26} style={{ display: "block", transform: "rotate(-90deg)" }}>
        <circle cx={13} cy={13} r={r} fill="none" stroke={alpha(color, 0.14)} strokeWidth={2} />
        <circle
          cx={13} cy={13} r={r} fill="none" stroke={color} strokeWidth={2}
          strokeLinecap="round" strokeDasharray={`${dash} ${circ}`}
          style={{ transition: "stroke-dasharray 0.15s ease, stroke 0.15s ease" }}
        />
      </svg>
      {nearLimit && (
        <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ fontSize: "0.5rem", fontWeight: 700, color, lineHeight: 1, fontFamily: FONT.body }}>
            {remaining < 0 ? remaining : remaining <= 20 ? remaining : ""}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────
   StoriesRow
───────────────────────────────────────────────────────── */
function StoriesRow({ feed }) {
  const theme = useTheme();
  const uniquePosters = useMemo(() => {
    const seen = new Set();
    return feed.filter((item) => {
      const id = item.creator_id || String(item.user_id || "");
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    }).slice(0, 14);
  }, [feed]);

  if (!uniquePosters.length) return null;

  return (
    <Box sx={{
      display: "flex", gap: 2.25,
      px: 2, pt: 1.75, pb: 2,
      overflowX: "auto",
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
      "&::-webkit-scrollbar": { display: "none" }, scrollbarWidth: "none",
      animation: `${fadeIn} 0.3s ease`,
    }}>
      {uniquePosters.map((item, i) => {
        const label       = (item.display_name || item.creator_id || "User").split(" ")[0];
        const hasActivity = item.total_reactions > 0 || item.reply_count > 0;
        return (
          <Box
            key={`story-${item.creator_id || item.user_id}-${i}`}
            sx={{
              display: "flex", flexDirection: "column", alignItems: "center",
              gap: 0.6, flexShrink: 0, cursor: "pointer",
              animation: `${fadeUp} 0.2s ease both`,
              animationDelay: `${i * 0.03}s`,
            }}
          >
            <Box sx={{
              p: "2px", borderRadius: "50%",
              background: hasActivity
                ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.light, 0.8)} 100%)`
                : alpha(theme.palette.divider, 0.8),
              transition: "transform 0.15s ease",
              "&:hover": { transform: "scale(1.05)" },
            }}>
              <Box sx={{ p: "2px", borderRadius: "50%", bgcolor: theme.palette.background.paper }}>
                <Avatar src={item.pfphref} sx={{ width: 42, height: 42 }} />
              </Box>
            </Box>
            <Typography noWrap sx={{
              fontSize: "0.68rem", maxWidth: 52, textAlign: "center",
              color: "text.secondary", fontWeight: 600, fontFamily: FONT.body,
            }}>
              {label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────
   NewPostsBanner
───────────────────────────────────────────────────────── */
function NewPostsBanner({ count, onClick }) {
  const theme = useTheme();
  if (!count || count <= 0) return null;
  return (
    <Box onClick={onClick} sx={{
      mx: 2, mt: 1.5, py: 0.875, px: 2, borderRadius: "8px",
      display: "flex", alignItems: "center", justifyContent: "center", gap: 0.875,
      bgcolor: theme.palette.primary.main, color: "white", cursor: "pointer",
      boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
      animation: `${bounceIn} 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)`,
      transition: "opacity 0.15s ease, transform 0.15s ease",
      "&:hover": { opacity: 0.9, transform: "translateY(-1px)" },
      "&:active": { transform: "scale(0.98)" },
    }}>
      <NewPostsIcon sx={{ fontSize: 15 }} />
      <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, fontFamily: FONT.body, letterSpacing: "-0.01em" }}>
        {count} new {count === 1 ? "post" : "posts"}
      </Typography>
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────
   PinnedReferral
───────────────────────────────────────────────────────── */
function PinnedReferral({ referralLink, onCopy, onViewReferrals, isGenerating }) {
  const theme = useTheme();
  return (
    <Box sx={{
      px: 2, pt: 1.75, pb: 2,
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
      transition: "background 0.12s",
      "&:hover": { bgcolor: alpha(theme.palette.action.hover, 0.3) },
    }}>
      {/* Pinned label */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1.25 }}>
        <PinIcon sx={{ fontSize: 11, color: "text.disabled", transform: "rotate(45deg)" }} />
        <Typography sx={{
          fontSize: "0.65rem", color: "text.disabled", fontWeight: 700,
          textTransform: "uppercase", letterSpacing: 1, fontFamily: FONT.body,
        }}>Pinned</Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 1.5 }}>
        {/* Icon */}
        <Box sx={{
          width: 40, height: 40, flexShrink: 0, borderRadius: "10px",
          border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          bgcolor: alpha(theme.palette.primary.main, 0.06),
        }}>
          <TrophyIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.4 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", fontFamily: FONT.display, letterSpacing: "-0.02em", color: "text.primary" }}>
              Referral Rewards
            </Typography>
            <Chip label="EARN" size="small" sx={{
              height: 18, fontSize: "0.6rem", fontWeight: 700, letterSpacing: 0.6,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              fontFamily: FONT.body,
              "& .MuiChip-label": { px: 0.7 },
            }} />
          </Box>
          <Typography sx={{
            fontSize: "0.875rem", color: "text.secondary", lineHeight: 1.5, mb: 1.25,
            fontFamily: FONT.body,
          }}>
            Invite friends to Blitz and earn{" "}
            <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>Blitz Points & cash</Box>{" "}
            for every signup.
          </Typography>
          <Box sx={{ display: "flex", gap: 0.75 }}>
            <Button
              size="small" variant="contained"
              startIcon={isGenerating ? <CircularProgress size={11} sx={{ color: "white" }} /> : <CopyIcon sx={{ fontSize: 13 }} />}
              onClick={onCopy} disabled={!referralLink || isGenerating}
              sx={{
                textTransform: "none", fontWeight: 600, fontSize: "0.8rem",
                fontFamily: FONT.body, borderRadius: "8px", px: 1.75, py: 0.5,
                boxShadow: "none", "&:hover": { boxShadow: "none" },
              }}
            >
              {isGenerating ? "Generating…" : "Copy link"}
            </Button>
            <Button
              size="small" variant="outlined"
              startIcon={<ViewIcon sx={{ fontSize: 13 }} />}
              onClick={onViewReferrals}
              sx={{
                textTransform: "none", fontWeight: 600, fontSize: "0.8rem",
                fontFamily: FONT.body, borderRadius: "8px", px: 1.75, py: 0.5,
                borderColor: alpha(theme.palette.divider, 0.8),
                color: "text.secondary",
                "&:hover": { borderColor: "text.secondary", bgcolor: "transparent" },
              }}
            >
              My referrals
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────
   Composer
───────────────────────────────────────────────────────── */
function Composer({ photo, text, setText, uploadedMedia, setUploadedMedia, onSubmit, isPosting, onCastingCallCreate, onOpenUpload }) {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const wrapRef  = useRef(null);
  const [open, setOpen] = useState(false);

  const showBar = open || text.length > 0 || !!uploadedMedia;
  const canPost = (text.trim() || uploadedMedia) && text.length <= MAX_CHARS && !isPosting;

  useEffect(() => {
    if (showBar) scrollAboveNav(wrapRef.current, isMobile);
  }, [showBar, isMobile]);

  return (
    <Box
      ref={wrapRef}
      sx={{
        px: 2, pt: 1.75, pb: showBar ? 1.75 : 1.5,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        transition: "background 0.2s ease",
        bgcolor: open ? alpha(theme.palette.primary.main, 0.015) : "transparent",
      }}
    >
      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Avatar
          src={photo}
          sx={{
            width: 40, height: 40, flexShrink: 0, mt: 0.25,
            border: `1.5px solid`,
            borderColor: open ? alpha(theme.palette.primary.main, 0.4) : alpha(theme.palette.divider, 0.6),
            transition: "border-color 0.2s ease",
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <TextField
            multiline minRows={showBar ? 2 : 1} maxRows={10} fullWidth
            value={text}
            onChange={(e) => setText(e.target.value)}
            onFocus={() => setOpen(true)}
            placeholder="What's happening?"
            variant="standard"
            InputProps={{
              disableUnderline: true,
              sx: {
                fontSize: { xs: "0.95rem", sm: "1rem" },
                fontFamily: FONT.body,
                lineHeight: 1.55,
                "& textarea": { padding: 0 },
                "& textarea::placeholder": {
                  color: alpha(theme.palette.text.secondary, 0.5), opacity: 1,
                  fontFamily: FONT.body,
                },
              },
            }}
          />

          {uploadedMedia && (
            <Box sx={{
              position: "relative", mt: 1.5, borderRadius: "10px", overflow: "hidden",
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              animation: `${fadeUp} 0.18s ease`,
            }}>
              <Box component="img" src={uploadedMedia.url} alt="attachment"
                sx={{ width: "100%", maxHeight: 280, objectFit: "cover", display: "block" }} />
              <IconButton size="small" onClick={() => setUploadedMedia(null)} sx={{
                position: "absolute", top: 8, right: 8,
                bgcolor: alpha("#000", 0.5), color: "white",
                "&:hover": { bgcolor: alpha("#000", 0.7) }, p: 0.5,
              }}>
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          )}

          <Collapse in={showBar} unmountOnExit>
            <Box sx={{
              display: "flex", alignItems: "center",
              mt: 1.25, pt: 1.25,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              animation: `${slideDown} 0.15s ease`,
            }}>
              <Box sx={{ display: "flex", gap: 0, flex: 1 }}>
                <Tooltip title="Add photo or video" placement="bottom">
                  <span>
                    <IconButton size="small" onClick={onOpenUpload} disabled={!!uploadedMedia} sx={{
                      color: "text.secondary", p: 0.875, borderRadius: "8px",
                      "&:hover": { bgcolor: alpha(theme.palette.action.hover, 0.6), color: theme.palette.primary.main },
                      "&:disabled": { opacity: 0.3 }, transition: "all 0.12s",
                    }}>
                      <ImageIcon sx={{ fontSize: 19 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Create Casting Call" placement="bottom">
                  <IconButton size="small" onClick={onCastingCallCreate} sx={{
                    color: "text.secondary", p: 0.875, borderRadius: "8px",
                    "&:hover": { bgcolor: alpha(theme.palette.action.hover, 0.6), color: theme.palette.primary.main },
                    transition: "all 0.12s",
                  }}>
                    <CampaignIcon sx={{ fontSize: 19 }} />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <CharCircle length={text.length} />
                {text.length > 0 && (
                  <Box sx={{ width: 1, height: 18, bgcolor: alpha(theme.palette.divider, 0.7) }} />
                )}
                <Button
                  variant="contained" onClick={onSubmit} disabled={!canPost}
                  sx={{
                    borderRadius: "8px", fontWeight: 600, textTransform: "none",
                    fontSize: "0.875rem", fontFamily: FONT.body,
                    px: 2.5, py: 0.5, minWidth: 76,
                    boxShadow: "none",
                    "&:hover": { boxShadow: "none", opacity: 0.88 },
                    transition: "opacity 0.15s ease",
                  }}
                >
                  {isPosting ? <CircularProgress size={14} sx={{ color: "white" }} /> : "Post"}
                </Button>
              </Box>
            </Box>
          </Collapse>
        </Box>
      </Box>
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────
   SkeletonRow — matches FeedItem geometry exactly
───────────────────────────────────────────────────────── */
function SkeletonRow({ i }) {
  const theme    = useTheme();
  const hasMedia = i % 5 === 3;
  const lines    = 1 + (i % 3);
  return (
    <Box sx={{
      display: "flex", gap: 1.5,
      px: 2, py: 1.75,
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
      animation: `${fadeUp} 0.2s ease both`,
      animationDelay: `${i * 0.04}s`,
    }}>
      <Skeleton variant="circular" width={40} height={40} sx={{ flexShrink: 0, mt: 0.1 }} />
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.875 }}>
          <Skeleton width={`${22 + (i * 7) % 20}%`} height={13} sx={{ borderRadius: "4px" }} />
          <Skeleton width={`${8 + (i * 3) % 8}%`}   height={11} sx={{ borderRadius: "4px" }} />
        </Box>
        {Array.from({ length: lines }).map((_, j) => (
          <Skeleton key={j}
            width={j === lines - 1 ? `${48 + (i * 9) % 36}%` : `${80 + (i * 5) % 16}%`}
            height={13} sx={{ borderRadius: "4px", mt: j > 0 ? 0.4 : 0 }}
          />
        ))}
        {hasMedia && (
          <Skeleton variant="rounded" width="100%" height={152} sx={{ borderRadius: "10px", mt: 1.25 }} />
        )}
        <Box sx={{ display: "flex", gap: 1.25, mt: 1.25 }}>
          <Skeleton width={40} height={20} sx={{ borderRadius: "99px" }} />
          <Skeleton width={40} height={20} sx={{ borderRadius: "99px" }} />
          <Skeleton width={32} height={20} sx={{ borderRadius: "99px" }} />
        </Box>
      </Box>
    </Box>
  );
}


/* ─────────────────────────────────────────────────────────
   EmptyFeed
───────────────────────────────────────────────────────── */
function EmptyFeed({ tab }) {
  const theme = useTheme();
  return (
    <Box sx={{
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", py: 9, px: 3,
      animation: `${fadeUp} 0.25s ease`,
    }}>
      <Box sx={{
        width: 56, height: 56, borderRadius: "14px", mb: 2.25,
        display: "flex", alignItems: "center", justifyContent: "center",
        border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
        bgcolor: alpha(theme.palette.action.hover, 0.4),
      }}>
        <SparkleIcon sx={{ fontSize: 26, color: "text.secondary", opacity: 0.6 }} />
      </Box>
      <Typography sx={{
        fontWeight: 700, fontSize: "1rem", mb: 0.6,
        fontFamily: FONT.display, letterSpacing: "-0.02em", color: "text.primary",
      }}>
        {tab === 1 ? "No posts yet" : "Nothing here yet"}
      </Typography>
      <Typography sx={{
        fontSize: "0.875rem", color: "text.secondary", textAlign: "center",
        maxWidth: 240, lineHeight: 1.55, fontFamily: FONT.body,
      }}>
        {tab === 1 ? "Follow more creators to see their posts." : "Be the first to post something on Blitz."}
      </Typography>
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────
   MAIN ZAPFEED
───────────────────────────────────────────────────────── */
export default function ZapFeed({
  onCastingCallCreate,
  referralLink,
  onCopyReferralLink,
  onViewReferrals,
  isGeneratingReferral,
}) {
  const theme    = useTheme();
  const navigate = useNavigate();
  const qc       = useQueryClient();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { creatorToken, isAuthorized: creatorOK } = useCreatorAuth();
  const { isAuthorized: userOK, getCurrrentUser }  = useAuth();

  /* ── iOS push token ───────────────────────────────── */
  const isiOS = Capacitor.getPlatform() === "ios";
  const [apnsToken, setApnsToken] = useState(null);

  useEffect(() => {
    if (!isiOS) return;
    if (window._pendingApnsToken) setApnsToken(window._pendingApnsToken);
    Preferences.get({ key: "apns_token" }).then(({ value }) => value && setApnsToken(value));
  }, [isiOS]);

  useEffect(() => {
    if (!isiOS || !apnsToken || (!userOK && !creatorOK)) return;
    API.notifications
      .registerDeviceToken({ device_token: apnsToken, platform: "ios" })
      .then(() => { window._pendingApnsToken = null; })
      .catch(console.error);
  }, [isiOS, apnsToken, userOK, creatorOK]);

  /* ── Identity ─────────────────────────────────────── */
  const isCreator = !!creatorToken?.creator_user;
  let displayName = "Me";
  let photo;
  if (isCreator) {
    const c = creatorToken.creator_user;
    displayName = c.creator?.display_name || c.username || displayName;
    photo       = c.creator?.pfphref      || c.pfphref;
  } else if (userOK) {
    const u = getCurrrentUser();
    displayName = u?.first_name || u?.last_name
      ? `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim()
      : u?.username || displayName;
    photo = u?.phphref;
  }

  /* ── State ────────────────────────────────────────── */
  const [tab,           setTab]           = useState(0);
  const [text,          setText]          = useState("");
  const [uploadedMedia, setUploadedMedia] = useState(null);
  const [uploadOpen,    setUploadOpen]    = useState(false);
  const [snack,         setSnack]         = useState(null);
  const [newPostCount,  setNewPostCount]  = useState(0);
  const prevFeedLen = useRef(0);

  const [shareDialogOpen,   setShareDialogOpen]   = useState(false);
  const [shareActivity,     setShareActivity]     = useState(null);
  const [ccViewOpen,        setCcViewOpen]        = useState(false);
  const [selectedCc,        setSelectedCc]        = useState(null);
  const [ccPosterInfo,      setCcPosterInfo]      = useState(null);
  const [shareMenuAnchor,   setShareMenuAnchor]   = useState(null);
  const [shareMenuActivity, setShareMenuActivity] = useState(null);

  const containerRef = useRef(null);

  /* ── Feed query ───────────────────────────────────── */
  const qKey = queryKeys.creator.zapfeed({ rows: ROW_COUNT, show_replies: false });

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage, error, refetch } =
    useInfiniteQuery(
      qKey,
      ({ pageParam = 0 }) =>
        API.zapfeed.list({ page: pageParam, rows: ROW_COUNT, show_replies: false }),
      { getNextPageParam: (last, pages) => last.zapfeed.length === ROW_COUNT ? pages.length : undefined, staleTime: 45_000, cacheTime: 120_000, refetchOnWindowFocus: false }
    );

  useEffect(() => { if (userOK || creatorToken) refetch(); }, [userOK, creatorToken]);

  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!sentinelRef.current || !hasNextPage) return;
    const obs = new IntersectionObserver(([e]) => e.isIntersecting && fetchNextPage(), { rootMargin: "400px 0px 0px 0px" });
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [fetchNextPage, hasNextPage]);

  const feed = useMemo(
    () => data?.pages.flatMap((p) =>
      p.zapfeed
        .filter((i) => i.activity_type !== "Reply")
        .map((item) => ({ ...item, is_global: !item.bubble_id && item.is_global !== false, bubble_name: item.bubble_name || null }))
    ) ?? [],
    [data]
  );

  const visibleFeed = useMemo(() => {
    if (tab === 0) return feed;
    return feed.filter((i) => i.activity_type === "Post" || i.activity_type === "Achievement");
  }, [feed, tab]);

  useEffect(() => {
    if (!feed.length || isLoading) return;
    if (prevFeedLen.current === 0) { prevFeedLen.current = feed.length; return; }
    const diff = feed.length - prevFeedLen.current;
    if (diff > 0) setNewPostCount((n) => n + diff);
    prevFeedLen.current = feed.length;
  }, [feed.length, isLoading]);

  const handleScrollToTop = useCallback(() => {
    containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setNewPostCount(0);
  }, []);

  /* ── Post mutation ────────────────────────────────── */
  const createPost = useMutation(
    async ({ text: t, mediaData }) => {
      const d = { text: t || "" };
      if (mediaData) { d.media_url = mediaData.url; d.media_type = mediaData.type; }
      return API.zapfeed.post(d);
    },
    {
      onSuccess: ({ post }) => {
        const newItem = {
          activity_id: post.id, activity_name: post.text, activity_type: "Post",
          activity_date: post.created_at,
          creator_id:   isCreator ? creatorToken.creator_user.username : null,
          user_id:      !isCreator && userOK ? getCurrrentUser()?.id : null,
          pfphref: photo, display_name: displayName,
          is_private: false, is_global: true,
          reaction_counts: {}, total_reactions: 0, user_reaction: null,
          can_react: true, reply_count: 0,
          media_url: post.media_url, media_type: post.media_type,
          media_width: post.media_width, media_height: post.media_height,
        };
        qc.setQueryData(qKey, (old) => {
          if (!old) return old;
          return { ...old, pages: [{ ...old.pages[0], zapfeed: [newItem, ...old.pages[0].zapfeed] }, ...old.pages.slice(1)] };
        });
        qc.invalidateQueries(queryKeys.creator.zapfeed());
        setText(""); setUploadedMedia(null);
        setSnack({ msg: "Posted!", sev: "success" });
      },
      onError: (err) => {
        const msg   = err?.response?.data?.error || "";
        const is429 = [409, 500].includes(err?.response?.status) ||
          ["already posted", "posted today", "once per day", "daily"].some((s) => msg.toLowerCase().includes(s));
        setSnack({ msg: is429 ? "You can only post once per day." : msg || "Something went wrong.", sev: is429 ? "warning" : "error" });
      },
    }
  );

  const handleSubmit = useCallback(() => {
    const t = text.trim();
    if ((!t && !uploadedMedia) || text.length > MAX_CHARS || createPost.isLoading) return;
    createPost.mutate({ text: t, mediaData: uploadedMedia });
  }, [text, uploadedMedia, createPost]);

  /* ── FeedItem callbacks ───────────────────────────── */
  const handlePitchClick = useCallback((a) => {
    if (!isCreator) return;
    const id = a?.activity_id; if (!id) return;
    if (["Campaign", "Pitch"].includes(a.activity_type)) navigate(`/creatorconnect/pitch/campaign/${id}`);
    else if (a.activity_type === "Partnership") navigate(`/creatorconnect/pitch/partnership/${id}`);
  }, [isCreator, navigate]);

  const handleCastingCallClick = useCallback((activity) => {
    if (activity.casting_call) {
      setSelectedCc(activity.casting_call);
      setCcPosterInfo({ pfphref: activity.pfphref, display_name: activity.display_name });
      setCcViewOpen(true);
    }
  }, []);

  const handleCastingCallShareClick = useCallback((e, activity) => {
    e.stopPropagation(); setShareMenuActivity(activity); setShareMenuAnchor(e.currentTarget);
  }, []);

  const handleShareMenuClose = useCallback(() => { setShareMenuAnchor(null); setShareMenuActivity(null); }, []);

  const handleCopyPublicLink = useCallback(async () => {
    const slug = shareMenuActivity?.casting_call?.public_slug;
    if (slug) {
      try {
        await navigator.clipboard.writeText(`${window.location.origin}/casting-calls/public/${slug}`);
        setSnack({ msg: "Link copied!", sev: "success" });
      } catch { setSnack({ msg: "Failed to copy", sev: "error" }); }
    }
    handleShareMenuClose();
  }, [shareMenuActivity, handleShareMenuClose]);

  const handleOpenInNewTab = useCallback(() => {
    const slug = shareMenuActivity?.casting_call?.public_slug;
    if (slug) window.open(`/casting-calls/public/${slug}`, "_blank");
    handleShareMenuClose();
  }, [shareMenuActivity, handleShareMenuClose]);

  const handleShare = useCallback((activity) => { setShareActivity(activity); setShareDialogOpen(true); }, []);

  const handleReactionChange = useCallback((data) => {
    const type = data?.activity?.activity_type;
    setSnack({
      msg: data.reaction ? (type === "Achievement" ? "Achievement celebrated!" : "Reaction added!") : "Reaction removed",
      sev: "success",
    });
  }, []);

  /* ── Render ───────────────────────────────────────── */
  return (
    <Box ref={containerRef} sx={{ maxWidth: 640, mx: "auto" }}>
      <style>{FONT_STYLE}</style>

      
      <Composer
        photo={photo} text={text} setText={setText}
        uploadedMedia={uploadedMedia} setUploadedMedia={setUploadedMedia}
        onSubmit={handleSubmit} isPosting={createPost.isLoading}
        onCastingCallCreate={onCastingCallCreate}
        onOpenUpload={() => setUploadOpen(true)}
      />

      {referralLink !== undefined && (
        <PinnedReferral
          referralLink={referralLink} onCopy={onCopyReferralLink}
          onViewReferrals={onViewReferrals} isGenerating={isGeneratingReferral}
        />
      )}

      <List sx={{ p: 0 }}>
        {isLoading ? (
          Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} i={i} />)
        ) : visibleFeed.length === 0 ? (
          <EmptyFeed tab={tab} />
        ) : (
          visibleFeed.map((act) => (
            <FeedItem
              key={`${act.activity_type}-${act.activity_id ?? act.creator_id ?? act.user_id ?? act.activity_date}`}
              activity={act}
              onPitchClick={handlePitchClick}
              onCastingCallClick={handleCastingCallClick}
              onCastingCallShareClick={handleCastingCallShareClick}
              onShare={handleShare}
              onReactionChange={handleReactionChange}
              userAuth={{ userOK, creatorOK }}
              queryClient={qc}
              onSnackbarMessage={(msg, sev) => setSnack({ msg, sev })}
              isCreator={isCreator}
            />
          ))
        )}

        {isFetchingNextPage && Array.from({ length: 3 }).map((_, i) => <SkeletonRow key={`more-${i}`} i={i} />)}

        <Box ref={sentinelRef} sx={{ height: 2 }} />

        {error && (
          <Box sx={{ px: 2, py: 5, textAlign: "center" }}>
            <Typography sx={{ fontSize: "0.875rem", color: "text.secondary", fontFamily: FONT.body }}>
              Something went wrong loading the feed.
            </Typography>
            <Button size="small" onClick={() => refetch()} sx={{
              mt: 1.25, textTransform: "none", fontWeight: 600,
              borderRadius: "8px", fontFamily: FONT.body,
            }}>
              Try again
            </Button>
          </Box>
        )}

        {!isLoading && !hasNextPage && visibleFeed.length > 0 && (
          <Box sx={{ py: 5, textAlign: "center" }}>
            <Typography sx={{
              display: "inline-block",
              fontSize: "0.8rem", color: "text.disabled",
              fontFamily: FONT.body, fontWeight: 500,
              px: 2, py: 0.75,
              border: (t) => `1px solid ${alpha(t.palette.divider, 0.5)}`,
              borderRadius: "8px",
            }}>
              You're all caught up
            </Typography>
          </Box>
        )}

        <Box sx={{ height: isMobile ? `calc(${MOBILE_NAV_OFFSET}px + env(safe-area-inset-bottom, 0px) + 16px)` : 32 }} />
      </List>

      {/* Dialogs */}
      <UploadFileDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploadSuccess={(f) => {
          const type = f.url?.toLowerCase().includes(".gif") || f.file_type === "gif" ? "gif" : "image";
          setUploadedMedia({ url: f.url, type });
          setSnack({ msg: "Image attached!", sev: "success" });
        }}
      />

      {shareActivity && (
        <ThreadShareDialog
          open={shareDialogOpen}
          onClose={() => { setShareDialogOpen(false); setShareActivity(null); }}
          activity={shareActivity}
        />
      )}

      <CastingCallDialog
        open={ccViewOpen}
        onClose={() => { setCcViewOpen(false); setSelectedCc(null); setCcPosterInfo(null); }}
        castingCall={selectedCc} posterInfo={ccPosterInfo}
      />

      <Menu
        anchorEl={shareMenuAnchor} open={Boolean(shareMenuAnchor)} onClose={handleShareMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{ sx: {
          borderRadius: "10px",
          boxShadow: "0 4px 24px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.06)",
          minWidth: 176,
          border: (t) => `1px solid ${alpha(t.palette.divider, 0.6)}`,
        }}}
      >
        <MenuItem onClick={handleCopyPublicLink} sx={{ py: 0.875, borderRadius: "6px", mx: 0.5 }}>
          <ListItemIcon><LinkIcon fontSize="small" /></ListItemIcon>
          <ListItemText
            primary="Copy public link"
            primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 500, fontFamily: FONT.body }}
          />
        </MenuItem>
        <MenuItem onClick={handleOpenInNewTab} sx={{ py: 0.875, borderRadius: "6px", mx: 0.5 }}>
          <ListItemIcon><OpenInNewIcon fontSize="small" /></ListItemIcon>
          <ListItemText
            primary="Open in new tab"
            primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 500, fontFamily: FONT.body }}
          />
        </MenuItem>
      </Menu>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{
          bottom: isMobile
            ? `calc(${MOBILE_NAV_OFFSET}px + env(safe-area-inset-bottom, 0px) + 10px) !important`
            : "24px !important",
        }}
      >
        {snack && (
          <Alert severity={snack.sev} variant="filled" onClose={() => setSnack(null)}
            sx={{
              width: "100%", fontFamily: FONT.body, fontWeight: 500,
              fontSize: "0.875rem", borderRadius: "10px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            }}>
            {snack.msg}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
}